/**
 * @file 状态页：403 / 404 / 未开放
 * 403 必须给出「无权限，请联系管理员」的明确出口（规范 4.4 权限收敛的配套体验），
 * 而不是把用户丢回首页——否则用户会以为按钮坏了。
 *
 * 版式：左对齐、限宽 56ch，读起来像一页说明而不是居中的弹窗；错误码只做小号标注。
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * @what 通用状态页骨架
 * @param props.code 错误码标注
 * @param props.title 标题
 * @param props.text 说明
 * @param props.action 操作区
 * @return JSX
 */
function StatePage({ code, title, text, action }: { code: string; title: string; text: string; action: ReactNode }) {
  return (
    <div className="lx-state">
      <div className="lx-state__code">{code}</div>
      <h1 className="lx-state__title">{title}</h1>
      <p className="lx-state__text">{text}</p>
      <div className="lx-state__actions">{action}</div>
    </div>
  );
}

/**
 * @what 403：有登录态但缺少功能权限，或超出数据范围
 * @return JSX
 */
export function ForbiddenPage() {
  return (
    <StatePage
      code="403"
      title="这个页面不在你的权限范围"
      text="当前账号没有该功能的权限码，或者这条数据不在你的数据范围内。左侧菜单已按权限隐藏，直接输链接同样会被拦截。需要开通请找管理员调整角色。"
      action={
        <>
          <Link className="lx-btn lx-btn--primary" to="/">
            返回工作台
          </Link>
          <Link className="lx-btn lx-btn--ghost" to="/password">
            账号安全
          </Link>
        </>
      }
    />
  );
}

/**
 * @what 404：路由不存在（D2 之后新增页面未上线时也会落到这里）
 * @return JSX
 */
export function NotFoundPage() {
  return (
    <StatePage
      code="404"
      title="没有这个页面"
      text="链接可能已经调整过，或者这个功能还没上线。当前版本开放的是登录、工作台和账号安全。"
      action={
        <Link className="lx-btn lx-btn--primary" to="/">
          返回工作台
        </Link>
      }
    />
  );
}

/**
 * @what 占位页：菜单入口已按权限渲染，但业务页面属于后续排期
 * @param props.moduleName 模块名
 * @return JSX
 */
export function SoonPage({ moduleName }: { moduleName: string }) {
  return (
    <StatePage
      code="未开放"
      title={`${moduleName}还在开发中`}
      text="入口和权限判定已经就绪，页面按 15 天计划排期上线。这里不展示任何模拟数据，避免把样例当成真实业务。"
      action={
        <Link className="lx-btn lx-btn--primary" to="/">
          返回工作台
        </Link>
      }
    />
  );
}
